import { apiClient, getAuthHeaders } from './api';

// Payment Configuration
export interface PaymentConfig {
  client_key: string;
  is_sandbox: boolean;
  environment: string;
  snap_js_url: string;
}

// Payment Transaction
export interface PaymentTransaction {
  id: string;
  invoice_id: string;
  order_id: string;
  midtrans_order_id: string;
  midtrans_transaction_id: string | null;
  snap_token: string | null;
  redirect_url: string | null;
  amount: number;
  currency: string;
  formatted_amount: string;
  payment_method: string | null;
  payment_type: string | null;
  status: 'PENDING' | 'SUCCESS' | 'FAILED' | 'CANCELLED' | 'EXPIRED';
  midtrans_status: string | null;
  midtrans_fraud_status: string | null;
  customer_name: string;
  customer_email: string;
  customer_phone: string | null;
  is_sandbox: boolean;
  is_pending: boolean;
  is_successful: boolean;
  is_failed: boolean;
  is_expired: boolean;
  created_at: string;
  updated_at: string;
  paid_at: string | null;
  expired_at: string | null;
  snap_payment_url: string;
}

// API Request/Response Types
export interface PaymentInitiateRequest {
  invoice_id: string;
  return_url?: string;
}

export interface PaymentInitiateResponse {
  success: boolean;
  message: string;
  payment_transaction?: PaymentTransaction;
  snap_token?: string;
  redirect_url?: string;
}

export interface PaymentStatusResponse {
  success: boolean;
  message: string;
  payment_transaction?: PaymentTransaction;
}

export interface PaymentListResponse {
  success: boolean;
  message: string;
  total: number;
  skip: number;
  limit: number;
  transactions: PaymentTransaction[];
}

// Midtrans Snap Response Types
export interface SnapCallbackResult {
  order_id: string;
  status_code: string;
  transaction_id: string;
  transaction_status: string;
  fraud_status?: string;
  payment_type?: string;
  gross_amount: string;
}

// Payment Service Class
export class PaymentService {
  private static instance: PaymentService;
  private paymentConfig: PaymentConfig | null = null;
  private snapLoaded = false;

  public static getInstance(): PaymentService {
    if (!PaymentService.instance) {
      PaymentService.instance = new PaymentService();
    }
    return PaymentService.instance;
  }

  /**
   * Get payment configuration from backend
   */
  async getPaymentConfig(): Promise<PaymentConfig> {
    try {
      if (!this.paymentConfig) {
        const response = await apiClient.get<PaymentConfig>(
          '/payments/config',
          getAuthHeaders()
        );
        this.paymentConfig = response;
      }
      return this.paymentConfig;
    } catch (error) {
      console.error('Failed to get payment config:', error);
      throw new Error('Gagal mengambil konfigurasi pembayaran');
    }
  }

  /**
   * Load Midtrans Snap.js library
   */
  async loadSnapScript(): Promise<void> {
    if (this.snapLoaded) {
      return;
    }

    try {
      const config = await this.getPaymentConfig();
      
      return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = config.snap_js_url;
        script.type = 'text/javascript';
        script.setAttribute('data-client-key', config.client_key);
        
        script.onload = () => {
          this.snapLoaded = true;
          resolve();
        };
        
        script.onerror = () => {
          reject(new Error('Gagal memuat library pembayaran'));
        };
        
        document.head.appendChild(script);
      });
    } catch (error) {
      console.error('Failed to load Snap script:', error);
      throw error;
    }
  }

  /**
   * Initiate payment for an invoice
   */
  async initiatePayment(request: PaymentInitiateRequest): Promise<PaymentInitiateResponse> {
    try {
      const response = await apiClient.post<PaymentInitiateResponse>(
        '/payments/initiate',
        request,
        getAuthHeaders()
      );
      return response;
    } catch (error: any) {
      console.error('Payment initiation failed:', error);
      const message = error.response?.data?.detail || 'Gagal memulai pembayaran';
      throw new Error(message);
    }
  }

  /**
   * Process payment using Midtrans Snap
   */
  async processPayment(
    snapToken: string,
    onSuccess?: (result: SnapCallbackResult) => void,
    onPending?: (result: SnapCallbackResult) => void,
    onError?: (result: SnapCallbackResult) => void,
    onClose?: () => void
  ): Promise<void> {
    try {
      // Ensure Snap.js is loaded
      await this.loadSnapScript();

      // Check if snap is available
      if (!window.snap) {
        throw new Error('Library pembayaran tidak tersedia');
      }

      // Process payment
      window.snap.pay(snapToken, {
        onSuccess: (result: SnapCallbackResult) => {
          console.log('Payment success:', result);
          if (onSuccess) onSuccess(result);
        },
        onPending: (result: SnapCallbackResult) => {
          console.log('Payment pending:', result);
          if (onPending) onPending(result);
        },
        onError: (result: SnapCallbackResult) => {
          console.error('Payment error:', result);
          if (onError) onError(result);
        },
        onClose: () => {
          console.log('Payment popup closed');
          if (onClose) onClose();
        }
      });
    } catch (error) {
      console.error('Payment processing failed:', error);
      throw error;
    }
  }

  /**
   * Get payment status by transaction ID
   */
  async getPaymentStatus(transactionId: string): Promise<PaymentStatusResponse> {
    try {
      const response = await apiClient.get<PaymentStatusResponse>(
        `/payments/status/${transactionId}`,
        getAuthHeaders()
      );
      return response;
    } catch (error: any) {
      console.error('Failed to get payment status:', error);
      const message = error.response?.data?.detail || 'Gagal mengambil status pembayaran';
      throw new Error(message);
    }
  }

  /**
   * Get payment transaction for an invoice
   */
  async getPaymentByInvoice(invoiceId: string): Promise<PaymentStatusResponse> {
    try {
      const response = await apiClient.get<PaymentStatusResponse>(
        `/payments/invoice/${invoiceId}`,
        getAuthHeaders()
      );
      return response;
    } catch (error: any) {
      console.error('Failed to get payment by invoice:', error);
      const message = error.response?.data?.detail || 'Gagal mengambil informasi pembayaran';
      throw new Error(message);
    }
  }

  /**
   * Get payment transactions list
   */
  async getPaymentTransactions(skip = 0, limit = 20): Promise<PaymentListResponse> {
    try {
      const response = await apiClient.get<PaymentListResponse>(
        `/payments/transactions?skip=${skip}&limit=${limit}`,
        getAuthHeaders()
      );
      return response;
    } catch (error: any) {
      console.error('Failed to get payment transactions:', error);
      const message = error.response?.data?.detail || 'Gagal mengambil daftar pembayaran';
      throw new Error(message);
    }
  }

  /**
   * Check if invoice can accept payment
   */
  canInvoiceAcceptPayment(invoiceStatus: string, isOpen: boolean): boolean {
    return invoiceStatus === 'POSTED' && isOpen;
  }

  /**
   * Verify and sync payment status from Midtrans using the order_id.
   * Called when user returns from Midtrans payment page.
   * Does NOT require authentication.
   */
  async verifyPaymentByOrderId(orderId: string): Promise<PaymentStatusResponse> {
    try {
      const response = await apiClient.post<PaymentStatusResponse>(
        '/payments/verify-by-order-id',
        { order_id: orderId }
      );
      return response;
    } catch (error: any) {
      console.error('Failed to verify payment by order ID:', error);
      const message = error.response?.data?.detail || 'Gagal memverifikasi status pembayaran';
      throw new Error(message);
    }
  }

  /**
   * Get payment status display text
   */
  getPaymentStatusText(status: string, language: 'en' | 'id' = 'id'): string {
    const statusTexts = {
      en: {
        PENDING: 'Pending',
        SUCCESS: 'Paid',
        FAILED: 'Failed',
        CANCELLED: 'Cancelled',
        EXPIRED: 'Expired'
      },
      id: {
        PENDING: 'Menunggu',
        SUCCESS: 'Dibayar',
        FAILED: 'Gagal',
        CANCELLED: 'Dibatalkan',
        EXPIRED: 'Kadaluarsa'
      }
    };
    
    return statusTexts[language][status as keyof typeof statusTexts.en] || status;
  }

  /**
   * Get payment status color class
   */
  getPaymentStatusColor(status: string): string {
    const colorMap: Record<string, string> = {
      PENDING: 'text-yellow-600 bg-yellow-50',
      SUCCESS: 'text-green-600 bg-green-50',
      FAILED: 'text-red-600 bg-red-50',
      CANCELLED: 'text-gray-600 bg-gray-50',
      EXPIRED: 'text-red-600 bg-red-50'
    };
    
    return colorMap[status] || 'text-gray-600 bg-gray-50';
  }

  /**
   * Format payment amount
   */
  formatAmount(amount: number, currency = 'IDR'): string {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  }

  /**
   * Get payment instructions text
   */
  getPaymentInstructions(language: 'en' | 'id' = 'id'): string {
    const instructions = {
      en: [
        '1. Click the "Pay Now" button to proceed with payment',
        '2. Choose your preferred payment method',
        '3. Complete the payment process',
        '4. You will receive a confirmation once payment is successful'
      ],
      id: [
        '1. Klik tombol "Bayar Sekarang" untuk melanjutkan pembayaran',
        '2. Pilih metode pembayaran yang Anda inginkan', 
        '3. Selesaikan proses pembayaran',
        '4. Anda akan menerima konfirmasi setelah pembayaran berhasil'
      ]
    };
    
    return instructions[language].join('\n');
  }
}

// Global payment service instance
export const paymentService = PaymentService.getInstance();

// Export default
export default paymentService;

// Extend Window interface for Snap.js
declare global {
  interface Window {
    snap: {
      pay: (snapToken: string, options: {
        onSuccess?: (result: SnapCallbackResult) => void;
        onPending?: (result: SnapCallbackResult) => void;
        onError?: (result: SnapCallbackResult) => void;
        onClose?: () => void;
      }) => void;
    };
  }
}